import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef, type ComponentType } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthGuard } from '@/components/auth-guard';
import { RoleGuard } from '@/components/role-guard';
import { AppLayout } from '@/components/layout/app-layout';
import { MobileLayout } from '@/components/layout/mobile-layout';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useAuthStore } from '@/store/auth-store';
import { useThemeStore, applyTheme } from '@/store/theme-store';
import { useVehiclesStore } from '@/store/vehicles-store';
import { useEventsStore } from '@/store/events-store';
import { ws } from '@/lib/ws';

// named(): lazy-load komponen yang di-export bernama (bukan default). Path
// import HARUS literal supaya Vite bisa code-split jadi chunk terpisah â€”
// halaman berat (deck.gl/ag-grid/maps) hanya dimuat saat dibuka.
function named(factory: () => Promise<Record<string, ComponentType<unknown>>>, key: string) {
  return lazy(async () => ({ default: (await factory())[key] }));
}

const LoginPage = named(() => import('@/pages/login'), 'LoginPage');
const DashboardPage = named(() => import('@/pages/dashboard'), 'DashboardPage');
const MonitoringPage = named(() => import('@/pages/monitoring'), 'MonitoringPage');
const PlaybackPage = named(() => import('@/pages/playback'), 'PlaybackPage');
const TripReportPage = named(() => import('@/pages/trip-report'), 'TripReportPage');
const TemperatureReportPage = named(() => import('@/pages/reports'), 'TemperatureReportPage');
const StopParkReportPage = named(() => import('@/pages/reports'), 'StopParkReportPage');
const EventsReportPage = named(() => import('@/pages/reports'), 'EventsReportPage');
const UnileverShipmentReportPage = named(() => import('@/pages/reports'), 'UnileverShipmentReportPage');
const UsersPage = named(() => import('@/pages/users'), 'UsersPage');
const VehiclesPage = named(() => import('@/pages/vehicles'), 'VehiclesPage');
const DeviceMembersPage = named(() => import('@/pages/device-members'), 'DeviceMembersPage');
const MaintenancePage = named(() => import('@/pages/maintenance'), 'MaintenancePage');
const RentalPage = named(() => import('@/pages/rental'), 'RentalPage');
const RentalReturnPage = named(() => import('@/pages/rental-return'), 'RentalReturnPage');
const RentalReportPage = named(() => import('@/pages/rental-report'), 'RentalReportPage');
const RentalChecklistMasterPage = named(() => import('@/pages/rental-checklist'), 'RentalChecklistMasterPage');
const VehicleCostPage = named(() => import('@/pages/vehicle-cost'), 'VehicleCostPage');
const InvoicePage = named(() => import('@/pages/invoice'), 'InvoicePage');
const ApiKeysPage = named(() => import('@/pages/api-keys'), 'ApiKeysPage');
const UnileverSettingPage = named(() => import('@/pages/unilever-setting'), 'UnileverSettingPage');
const UnileverShipmentPage = named(() => import('@/pages/unilever-shipment'), 'UnileverShipmentPage');
const StanleyPage = named(() => import('@/pages/stanley'), 'StanleyPage');
const VehicleBrandsPage = named(() => import('@/pages/vehicle-brands'), 'VehicleBrandsPage');
const GpsBrandsPage = named(() => import('@/pages/gps-brands'), 'GpsBrandsPage');
const PlatformProfilePage = named(() => import('@/pages/platform-profile'), 'PlatformProfilePage');
const ProfilePage = named(() => import('@/pages/profile'), 'ProfilePage');
const PublicSharePage = named(() => import('@/pages/public-share'), 'PublicSharePage');
const ServiceRecordPage = named(() => import('@/pages/service-record'), 'ServiceRecordPage');
const NotificationPrefPage = named(() => import('@/pages/notification-pref'), 'NotificationPrefPage');
const WhatsAppSetupPage = named(() => import('@/pages/whatsapp-setup'), 'WhatsAppSetupPage');
const TenantsPage = named(() => import('@/pages/tenants'), 'TenantsPage');
const ActiveSharesPage = named(() => import('@/pages/active-shares'), 'ActiveSharesPage');
const ApiDocPage = named(() => import('@/pages/api-doc'), 'ApiDocPage');

// Halaman mobile (shell MobileLayout).
const MobileHomePage = named(() => import('@/pages/mobile/mobile-home'), 'MobileHomePage');
const MobileDevicesPage = named(() => import('@/pages/mobile/mobile-devices'), 'MobileDevicesPage');
const MobileMapPage = named(() => import('@/pages/mobile/mobile-map'), 'MobileMapPage');
const MobileEventsPage = named(() => import('@/pages/mobile/mobile-events'), 'MobileEventsPage');
const MobileProfilePage = named(() => import('@/pages/mobile/mobile-profile'), 'MobileProfilePage');

// =============================================================================
// Top-level routing:
//   /login                        â€” public
//   /  (semua route lain)         â€” wrap dengan AuthGuard + AppLayout
//
// Halaman yang belum diimplementasi diberi placeholder agar sidebar tetap clickable.
// =============================================================================

function Placeholder({ title }: { title: string }) {
  return (
    <div className="h-full flex items-center justify-center text-muted-foreground">
      <div className="text-center">
        <h2 className="text-xl font-semibold">{title}</h2>
        <p className="text-sm mt-1">Halaman ini belum dikerjakan.</p>
      </div>
    </div>
  );
}

export default function App() {
  const userId = useAuthStore((s) => s.effectiveUserId());
  const theme = useThemeStore((s) => s.theme);
  const qc = useQueryClient();
  const prevUserIdRef = useRef<number | null>(null);
  const isMobile = useIsMobile();

  // Connect WS saat user authenticated; reconnect kalau effectiveUserId berubah
  // (mis. admin switch user via UserSwitcher). Auto-reconnect dengan
  // exponential backoff di-handle internal oleh ws client (lihat lib/ws.ts).
  useEffect(() => {
    if (userId) {
      ws.connect(userId);
      return () => ws.disconnect();
    }
  }, [userId]);

  // Saat WS pulih setelah putus (api-server sempat down / network drop), data
  // live mungkin ketinggalan selama koneksi mati. Refetch semua query aktif
  // supaya peta + list langsung sinkron lagi, lalu frame WS lanjut realtime.
  useEffect(() => ws.onReconnected(() => { qc.invalidateQueries(); }), [qc]);

  // Apply theme on mount + when changed. Listen ke perubahan prefers-color-scheme
  // OS saat theme='system' supaya UI mengikuti dark/light system langsung.
  useEffect(() => {
    applyTheme(theme);
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const handler = () => applyTheme('system');
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
  }, [theme]);

  // Clear per-user state setiap kali effective user berubah (login awal,
  // switch user, atau logout-login user lain). Tanpa ini, data user lama
  // tetap visible singkat sampai query baru selesai.
  //
  // Scope yang di-clear:
  //   - vehicles store    (peta + list device)
  //   - events store      (alarm history)
  //   - react-query cache (positions, vehicles, poi, geofences, rental, dst)
  //
  // prevUserIdRef cegah clear pada mount pertama (waktu prev=null â†’ user=X
  // bukan user-change, itu login awal jadi store memang kosong).
  useEffect(() => {
    const prev = prevUserIdRef.current;
    if (prev !== null && prev !== userId) {
      useVehiclesStore.getState().reset([]);
      useEventsStore.getState().clear();
      // Invalidate semua query â†’ react-query auto-refetch untuk page yang aktif.
      qc.invalidateQueries();
    }
    prevUserIdRef.current = userId;
  }, [userId, qc]);

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* Public share viewer â€” TIDAK perlu auth, di luar AppLayout */}
      <Route path="/public/share/:token" element={<PublicSharePage />} />

      {/* ===== MOBILE shell (perangkat mobile / ?ui=mobile) ===== */}
      {isMobile && (
        <Route element={<AuthGuard><MobileLayout /></AuthGuard>}>
          <Route index element={<Navigate to="/m/home" replace />} />
          <Route path="/m" element={<Navigate to="/m/home" replace />} />
          <Route path="/m/home" element={<MobileHomePage />} />
          <Route path="/m/devices" element={<MobileDevicesPage />} />
          <Route path="/m/map" element={<MobileMapPage />} />
          <Route path="/m/events" element={<MobileEventsPage />} />
          <Route path="/m/profile" element={<MobileProfilePage />} />
        </Route>
      )}

      {/* ===== DESKTOP shell ===== */}
      {!isMobile && (
      <Route
        element={
          <AuthGuard>
            <AppLayout />
          </AuthGuard>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard"     element={<DashboardPage />} />
        <Route path="/monitoring"    element={<MonitoringPage />} />
        <Route path="/playback"      element={<Navigate to="/reports/playback" replace />} />
        <Route path="/reports/playback" element={<PlaybackPage />} />
        <Route path="/reports/trip"  element={<TripReportPage />} />
        <Route path="/reports/temperature" element={<TemperatureReportPage />} />
        <Route path="/reports/stop-parkir" element={<StopParkReportPage />} />
        <Route path="/reports/events" element={<EventsReportPage />} />
        <Route path="/reports/unilever-shipment" element={<UnileverShipmentReportPage />} />
        <Route path="/vehicles"      element={<VehiclesPage />} />
        <Route path="/users"         element={<UsersPage />} />
        <Route path="/device-member" element={<RoleGuard levels={[1, 4]}><DeviceMembersPage /></RoleGuard>} />
        <Route path="/poi"           element={<Placeholder title="POI" />} />
        <Route path="/geofence"      element={<Placeholder title="Geofence" />} />
        <Route path="/maintenance"           element={<MaintenancePage />} />
        <Route path="/maintenance/schedule"  element={<MaintenancePage />} />
        <Route path="/maintenance/records"   element={<ServiceRecordPage />} />
        <Route path="/notification-pref"     element={<NotificationPrefPage />} />
        <Route path="/whatsapp-setup"        element={<RoleGuard levels={[1]}><WhatsAppSetupPage /></RoleGuard>} />
        <Route path="/tenants"               element={<RoleGuard levels={[1]}><TenantsPage /></RoleGuard>} />
        <Route path="/shares"                element={<ActiveSharesPage />} />
        <Route path="/rental"           element={<RentalPage />} />
        <Route path="/rental/return"    element={<RentalReturnPage />} />
        <Route path="/rental/report"    element={<RentalReportPage />} />
        <Route path="/rental/checklist" element={<RentalChecklistMasterPage />} />
        <Route path="/vehicle-cost"     element={<RoleGuard levels={[1]}><VehicleCostPage /></RoleGuard>} />
        <Route path="/invoice"          element={<RoleGuard levels={[1]}><InvoicePage /></RoleGuard>} />
        <Route path="/api-keys"         element={<RoleGuard levels={[1]}><ApiKeysPage /></RoleGuard>} />
        <Route path="/integration/unilever-setting"  element={<RoleGuard levels={[1, 4]}><UnileverSettingPage /></RoleGuard>} />
        <Route path="/integration/unilever-shipment" element={<RoleGuard levels={[1, 4]}><UnileverShipmentPage /></RoleGuard>} />
        <Route path="/integration/stanley"           element={<RoleGuard levels={[1, 4]}><StanleyPage /></RoleGuard>} />
        <Route path="/vehicle-brands"   element={<RoleGuard levels={[1]}><VehicleBrandsPage /></RoleGuard>} />
        <Route path="/gps-brands"       element={<RoleGuard levels={[1]}><GpsBrandsPage /></RoleGuard>} />
        <Route path="/platform-profile" element={<RoleGuard levels={[1]}><PlatformProfilePage /></RoleGuard>} />
        <Route path="/profile"          element={<ProfilePage />} />
        <Route path="/api-doc"          element={<ApiDocPage />} />
        <Route path="/vehicle-group" element={<Placeholder title="Vehicle Group" />} />
        <Route path="/events"        element={<Navigate to="/reports/events" replace />} />
      </Route>
      )}

      <Route path="*" element={<Navigate to={isMobile ? '/m/home' : '/dashboard'} replace />} />
    </Routes>
    </Suspense>
  );
}

// Fallback saat chunk halaman (lazy) sedang dimuat. Ringan, tanpa dependency.
function PageLoader() {
  return (
    <div className="h-screen w-screen flex items-center justify-center bg-background">
      <span className="inline-block w-6 h-6 border-2 border-muted-foreground/30 border-t-primary rounded-full animate-spin" />
    </div>
  );
}
